import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { 
  default as ibanService
} from "../services/iban";
import axios, { AxiosError } from "axios";
import { actions } from '../slices/elecContracts';
import { ContractDisplayTypes } from "../types/contracts";
import { IBANsFetchSucessResponse, ISendIBANChangeParams } from "../types/iban";
import { useContracts } from "./useContracts";

interface IUseIBANProps {
  type: ContractDisplayTypes;
}

type UseIBANState = "uninitialized" | "fetching" | "selecting" | "submitting" | "success" | "error";

const useIBAN = ({
  type = "electricity"
}: IUseIBANProps) => {
  const dispatch = useAppDispatch();

  const { currentContract } = useContracts(type);
  const waitingForSignature = currentContract?.waitingForSignature;

  const [ibans, setIbans] = useState<IBANsFetchSucessResponse>([]);
  const [errors, setErrors] = useState<AxiosError | undefined>();
  const [state, setState] = useState<UseIBANState>(
    waitingForSignature?.includes("iban") ? "success" : "uninitialized"
  );

  useEffect(() => {
    if (state === "uninitialized") {
      // TODO: catxejar
      fetchIBANs();
    }
  }, [state]);

  const fetchIBANs = async () => {
    setState("fetching");
    ibanService.fetchIBANs()
    .then(res => {
      setIbans(res.promise.data);
      setState("selecting");
    })
    .catch(err => {
      if (axios.isAxiosError(err))  {
        setErrors(err.response?.data.errors);
      }
      setState("error");
    })
  }

  const changeIBAN = async (
    contractName: string,
    value: ISendIBANChangeParams["value"]
  ) => {
    setState("submitting");

    ibanService.sendIBANChange({
      contractName,
      type,
      value
    })
    .then(async (res) => {
      const { waiting_for_signature } = res.promise.data;
      if (!waiting_for_signature) {
        //TODO: Display 'changes being made' with API returned data
      } else {
        dispatch(actions.signatureStarted("iban"));
        dispatch(actions.changeIbanSuccessed());
      }
      setState("success");
    })
    .catch(err => {
      if (axios.isAxiosError(err))  {
        const status = err.response?.status;

        if (status !== undefined && status >= 400 && status < 500) {
          setErrors(err.response?.data.errors);
        }
      }
      setState("error");
    })
  };

  const checkDiffIBAN = async (
    contractName: string,
    iban: string
  ) => {
    setState("submitting");

    return new Promise<boolean>((resolve, reject) => {
      ibanService.checkDiffIBAN({
        contractName,
        type,
        iban
      })
      .then(async (res) => {
        const { result } = res.promise.data;
        if(!result){
          setState("error");
        }
        resolve(result)
      })
      .catch(err => {
        if (axios.isAxiosError(err))  {
          if (err.response?.status === 400) {
            setErrors(err.response?.data.errors);
          }
        }
        setState("error");
        reject(err)
      })
    })
  };

  return {
    ibans,
    errors,
    state,
    waitingForSignature: waitingForSignature?.includes("iban") ?? false,
    fetchIBANs,
    changeIBAN,
    checkDiffIBAN
  }

}

export default useIBAN;
